/**
 * diameterHistogram.js
 *
 * Per-diameter bit data feed for the Step 4 hole-diameter UI. Returns the
 * distinct set of valid drill-bit diameters (mm) for the current product
 * line plus a per-diameter bit count map. Honours an `effectiveThreads`
 * filter so the slider tick-marks only show diameters reachable from the
 * user's thread choice.
 *
 * Repurposed from the pre-D5 bucketed-histogram implementation. The
 * dual-range-slider density bars are gone — the new StepHoleDiameter port
 * (TF v4 lines 40-372) consumes a list of discrete diameters with counts
 * instead. Source contract: TF v4 ProductContext.jsx lines 290-326
 * (availableDiameters + bitCountsByDiameter).
 *
 * Data field map (audited 17 May 2026 against public/data):
 *   - TH bits:  subcategory === 'drill-bits',  bitDiameterMm   (camelCase, top-level)
 *               thread filter: `thread` (e.g. 'R32', 'T38')
 *   - DTH bits: category === 'dth-bits',       bitDiameter_mm  (snake_case, top-level)
 *               (no top-level `thread`; DTH uses shankType — not used here)
 *
 * Zero-valued bitDiameterMm entries (rare placeholders on TH reamer bits)
 * are excluded — they're not real valid diameters and would skew popularity.
 *
 * Reads from wizardMatcher's lazy product caches via getProductsForLine().
 * Call ensureDiameterDataReady() once per component mount before invoking
 * getAvailableDiameters / getBitCountsByDiameter so the synchronous
 * getters always see populated data.
 *
 * @module services/diameterHistogram
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D5 REVERT)
 */

import {
  ensureProductsLoaded,
  getProductsForLine,
} from '@services/wizardMatcher';

/**
 * Return only the drill-bit products from a productLine's cache.
 * Tolerates the two field shapes (TH uses `subcategory`, DTH uses
 * `category === 'dth-bits'`).
 *
 * @param {Array<Object>} products
 * @param {'TH'|'DTH'} productLine
 * @returns {Array<Object>}
 */
function bitsOnly(products, productLine) {
  if (!Array.isArray(products)) return [];
  if (productLine === 'DTH') {
    return products.filter(p => p?.category === 'dth-bits');
  }
  // TH: bits are tagged on subcategory
  return products.filter(p => p?.subcategory === 'drill-bits' || p?.category === 'drill-bits');
}

/**
 * Pull the diameter (mm) from a bit product. Field name differs by line.
 *
 * @param {Object} product
 * @param {'TH'|'DTH'} productLine
 * @returns {number|null}
 */
function diameterMmOfBit(product, productLine) {
  if (!product) return null;
  if (productLine === 'DTH') {
    const d = product.bitDiameter_mm ?? product.dimensions?.bitDiameter_mm;
    return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : null;
  }
  const d = product.bitDiameterMm ?? product.bitDiameter_mm;
  return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : null;
}

/**
 * Filter bits by an effective-threads allowlist. Only meaningful for TH
 * (DTH bits don't carry a `thread` field). When threads is null / empty
 * the input list is returned unchanged.
 *
 * @param {Array<Object>} bits
 * @param {'TH'|'DTH'} productLine
 * @param {string[]|null|undefined} threads
 * @returns {Array<Object>}
 */
function filterByThreads(bits, productLine, threads) {
  if (productLine !== 'TH') return bits;
  if (!Array.isArray(threads) || threads.length === 0) return bits;
  const set = new Set(threads);
  return bits.filter(b => {
    const t = b?.thread || b?.threadSystem;
    return t && set.has(t);
  });
}

/**
 * Distinct sorted (ascending) list of valid hole diameters (mm) for the
 * given product line, optionally narrowed to a thread allowlist.
 *
 * @param {'TH'|'DTH'} productLine
 * @param {string[]|null} [effectiveThreads]  null/empty = no thread filter
 * @returns {number[]}
 */
export function getAvailableDiameters(productLine, effectiveThreads) {
  const products = getProductsForLine(productLine);
  if (!products) return [];
  const bits = filterByThreads(bitsOnly(products, productLine), productLine, effectiveThreads);
  const set = new Set();
  for (const b of bits) {
    const d = diameterMmOfBit(b, productLine);
    if (d != null) set.add(d);
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Bit count per diameter (mm). Same scope rules as getAvailableDiameters.
 *
 * @param {'TH'|'DTH'} productLine
 * @param {string[]|null} [effectiveThreads]
 * @returns {Record<number, number>}
 */
export function getBitCountsByDiameter(productLine, effectiveThreads) {
  const products = getProductsForLine(productLine);
  if (!products) return {};
  const bits = filterByThreads(bitsOnly(products, productLine), productLine, effectiveThreads);
  const counts = {};
  for (const b of bits) {
    const d = diameterMmOfBit(b, productLine);
    if (d == null) continue;
    counts[d] = (counts[d] || 0) + 1;
  }
  return counts;
}

/**
 * Ensures the underlying product caches are loaded. Idempotent — safe to
 * call from a useEffect on every render.
 *
 * @returns {Promise<void>}
 */
export async function ensureDiameterDataReady() {
  await ensureProductsLoaded();
}
